import { Link } from "react-router-dom";
import { Section, Eyebrow } from "@/components/Section";
import { ArrowUpRight } from "lucide-react";

const CREDENTIALS = [
  { label: "License", value: "LCSW · Colorado" },
  { label: "Education", value: "MSW, Clinical Practice" },
  { label: "Trained in", value: "EMDR · TF-CBT · CBT · DBT · MST · WET" },
  { label: "Ages served", value: "10 – 50" },
];

export default function About() {
  return (
    <>
      <Section testId="about-hero" className="!pt-24 !pb-12">
        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <Eyebrow>About Jeremy</Eyebrow>
            <h1 className="font-serif-display text-4xl sm:text-6xl lg:text-7xl mt-6 text-[#2E3630] leading-[1.02]">
              A quiet practitioner of a loud kind of work.
            </h1>
          </div>
          <div className="lg:col-span-4" />
        </div>
      </Section>

      <Section testId="about-bio" className="!pt-12">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <img
              src="https://customer-assets-gfyr7b9c.emergentagent.net/job_mindcare-clinic-8/artifacts/gudc3ph2_fuerte%20headshot.png"
              alt="Jeremy Fuerte, LCSW"
              data-testid="about-jeremy-photo"
              className="rounded-[2rem] w-full h-[560px] object-cover object-top sticky top-28"
            />
          </div>

          <div className="lg:col-span-7 space-y-8 font-body text-lg text-[#5F6B62] leading-relaxed">
            <p>
              I'm Jeremy Fuerte, a Licensed Clinical Social Worker based in
              Denver, Colorado. My practice is small by design — I see a
              limited caseload so the people I work with get the depth and
              consistency that trauma work requires.
            </p>
            <p>
              I came to this work through the long way: years of working with
              adolescents and young adults who were homeless and in the child
              welfare system, veterans who have served in war-zones, and
              survivors of domestic violence or sexual assault who had spent
              years being told their reactions were too big, too quiet, or too
              inconvenient. They weren't. Their nervous systems were doing
              exactly what survival required. My job is to help that system
              learn it's safe now and to help you create your own story and
              become the master of your own mind and body.
            </p>

            <div className="grid sm:grid-cols-2 gap-6 py-6">
              {CREDENTIALS.map((c) => (
                <div key={c.label} data-testid={`credential-${c.label.toLowerCase().replace(/\s+/g, "-")}`} className="border-l-2 border-[#6B8E7B] pl-5">
                  <p className="eyebrow text-[10px]">{c.label}</p>
                  <p className="font-serif-display text-2xl text-[#2E3630] mt-1">{c.value}</p>
                </div>
              ))}
            </div>

            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#2E3630] pt-4">My approach</h2>
            <p>
              I lean on evidence-based modalities — EMDR for trauma
              reprocessing, TF-CBT for younger clients, CBT and DBT for
              anxiety, depression, and emotional regulation — but I don't
              treat them as scripts. People aren't protocols. The relationship
              is the work. The techniques are the tools we use inside it.
            </p>
            <p>
              I tend toward slow, deliberate care. We will not rush. You will
              never be asked to share more than you are ready to share. The
              goal is not catharsis; it's integration — the kind that holds
              long after the session ends.
            </p>

            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#2E3630] pt-4">Who I work with</h2>
            <p>
              Adolescents and adults (10–50) navigating PTSD, depression,
              anxiety and grief. Survivors of assault or domestic violence.
              Veterans returning from service. LGBTQ+ clients who want a
              therapist they don't have to educate. Youth involved in the
              juvenile justice or child welfare system. First- and
              second-generation immigrants attempting to navigate life in
              Colorado. People who've tried therapy before and felt unseen.
            </p>

            <div className="pt-6">
              <Link to="/contact" data-testid="about-book-cta" className="btn-pill btn-primary">
                Request a consultation <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
}
